import { ContentBlock, ContentState, EditorState, Modifier } from 'draft-js';
import { EntityTypes, RichEditorVariable, TriggerRange } from './types';

interface EditorStateUpdate {
    changed: boolean;
    editorState: EditorState;
}

export const getVariableRegex = (token: string = '$'): RegExp => new RegExp(`\\${token}([\\w.]*)\\b|\\${token}`, 'g');
export const getWrappedVariableRegex = (start: string = '<%', end: string = '%>'): RegExp =>
    new RegExp(`${start} ([\\w.]*) ${end}`, 'g');

export const findWithRegex = (
    regex: RegExp,
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    test: (value: string, index?: number) => boolean
): void => {
    const text = contentBlock.getText();
    let matchArr, start;

    while ((matchArr = regex.exec(text)) !== null) {
        start = matchArr.index;
        const match = matchArr[0];
        if (match && test(match, start)) {
            callback(start, start + match.length);
        }
    }
};

export const findEntity = (
    contentBlock: ContentBlock,
    contentState: ContentState,
    entityType: EntityTypes,
    callback: (start: number, end: number) => void
): void => {
    return contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        if (entityKey === null) return false;
        return contentState.getEntity(entityKey).getType() === entityType;
    }, callback);
};

export const getTextTriggerRange = (editorState: EditorState, token: string = '$'): TriggerRange | undefined => {
    const offset = editorState.getSelection().getEndOffset();
    const startKey = editorState.getSelection().getStartKey();
    const currentBlock = editorState.getCurrentContent().getBlockForKey(startKey);
    const blockText = currentBlock.getText();
    const text = blockText.substring(0, offset);
    if (/\\s+$/.test(text)) return;
    const index = text.lastIndexOf(token);
    if (index === -1) return;
    if (text.substring(index).includes(' ')) return;

    const foundText = text.substring(index);

    const triggerRange = {
        text: foundText.slice(1, foundText.length),
        start: index,
        end: offset,
        blockKey: currentBlock.getKey(),
    };

    return triggerRange;
};

export const handlePaste = (
    text: string,
    editorState: EditorState,
    variables: RichEditorVariable[],
    removeLineBreaks: boolean = false
): EditorStateUpdate => {
    let lastState = editorState;
    let changed = false;

    if (removeLineBreaks) {
        text = text.replace(/\n/g, ' ');
        changed = true;
    }

    const pastedBlocks = ContentState.createFromText(text).getBlockMap();
    const newState = Modifier.replaceWithFragment(
        lastState.getCurrentContent(),
        lastState.getSelection(),
        pastedBlocks
    );
    lastState = EditorState.push(lastState, newState, 'insert-fragment');

    const blocks = lastState.getCurrentContent().getBlockMap().values();
    let blockItem = blocks.next();

    const blockKeys: string[] = [];

    while (!blockItem.done) {
        const block = blockItem.value;
        blockKeys.push(block.getKey());
        blockItem = blocks.next();
    }

    blockKeys.forEach((key) => {
        const content = lastState.getCurrentContent();
        const block = content.getBlockForKey(key);
        const blockText = block.getText();

        const searchResult = searchForVariablesInText(blockText, key, lastState, variables);
        lastState = searchResult.editorState;
        changed = changed || searchResult.changed;
    });

    return { changed, editorState: lastState };
};

export const searchForVariablesInText = (
    blockText: string,
    blockKey: string,
    editorState: EditorState,
    variables: RichEditorVariable[],
    token: string = '$'
): EditorStateUpdate => {
    let currentText = blockText;
    let lastState = editorState;
    let matchArr;
    let changed = false;

    const VARIABLE_REGEX = getVariableRegex(token);

    while ((matchArr = VARIABLE_REGEX.exec(currentText)) !== null) {
        const start = matchArr.index;
        const match = matchArr[0];
        if (match) {
            const foundVar = variables.find((variable) =>
                Array.isArray(variable.name)
                    ? variable.name.find((name) => `${token}${name}`.toLowerCase() === match.toLowerCase())
                    : `${token}${variable.name}`.toLowerCase() === match.toLowerCase()
            );

            if (foundVar) {
                changed = true;
                lastState = replaceWithVariable(lastState, foundVar, {
                    start: start,
                    end: start + match.length,
                    blockKey: blockKey,
                });
                currentText = lastState.getCurrentContent().getBlockForKey(blockKey).getText();
            }
        }
    }

    return { changed, editorState: lastState };
};

export const insertCharacters = (
    charactersToInsert: string,
    editorState: EditorState,
    options?: {
        entityKey?: string;
        range?: { start: number; end: number; blockKey?: string };
    }
): EditorState => {
    let currentSelection = editorState.getSelection();

    if (options?.range) {
        currentSelection = currentSelection.merge({
            focusOffset: options.range.end,
            anchorOffset: options.range.start,
            focusKey: options.range.blockKey ?? currentSelection.getFocusKey(),
            anchorKey: options.range.blockKey ?? currentSelection.getAnchorKey(),
        });
    }

    const currentContent = editorState.getCurrentContent();

    const newContent = Modifier.replaceText(
        currentContent,
        currentSelection,
        charactersToInsert,
        undefined,
        options?.entityKey
    );

    return EditorState.push(editorState, newContent, 'insert-characters');
};

export const replaceWithVariable = (
    editorState: EditorState,
    variable: RichEditorVariable,
    range: { start: number; end: number; blockKey?: string }
): EditorState => {
    const { value, ...variableWithoutValue } = variable;
    const content = editorState
        .getCurrentContent()
        .createEntity(EntityTypes.Variable, 'IMMUTABLE', variableWithoutValue);
    const entityKey = content.getLastCreatedEntityKey();

    const variableName = Array.isArray(variable.name) ? variable.name[0] : variable.name;

    return insertCharacters(variable.displayText ?? variableName ?? '', editorState, {
        range,
        entityKey,
    });
};

export const replaceWithLink = (
    editorState: EditorState,
    text: string,
    url: string,
    range?: { start: number; end: number; blockKey?: string }
): EditorState => {
    const content = editorState.getCurrentContent().createEntity(EntityTypes.Link, 'IMMUTABLE', { url });
    const entityKey = content.getLastCreatedEntityKey();

    return insertCharacters(text, editorState, {
        range,
        entityKey,
    });
};

export const handleTextInput = (
    text: string,
    editorState: EditorState,
    variables: RichEditorVariable[]
): EditorStateUpdate => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();

    // This is a hacky way to fix a crash on mac
    // Whenever you have the "double-space inserts a period" option enabled,
    // selecting a variable and pressing space twice would crash the editor.
    // See this comment for context: https://github.com/facebook/draft-js/issues/2171#issuecomment-631749909
    // And this for explanation on the fix: https://github.com/facebook/draft-js/issues/2422#issuecomment-683221263
    if (text === '. ') {
        return {
            changed: true,
            editorState: EditorState.set(editorState, {
                currentContent: Modifier.replaceText(content, selection, ' '),
            }),
        };
    }

    const block = content.getBlockForKey(selection.getAnchorKey());
    const blockText = `${block.getText()}${text}`;

    const newState = searchForVariablesInText(blockText, block.getKey(), editorState, variables);

    return newState;
};

export const getCurrentLinkData = (editorState: EditorState): { text: string; url: string } => {
    let text = '';
    let url = '';
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());
    const startOffset = selection.getStartOffset();
    const entityKey = block.getEntityAt(startOffset);
    const endOffset = selection.getEndOffset();

    text = !selection.isCollapsed() ? block.getText().slice(startOffset, endOffset) : '';

    if (entityKey) {
        const entity = content.getEntity(entityKey);
        if (entity.getType() === EntityTypes.Link) {
            text = entity.getData().text;
            url = entity.getData().url;
        }
    }

    return { text, url };
};

export const getCursorLocation = (containerRef: React.RefObject<HTMLDivElement>): { top: number; left: number } => {
    const selection = window.getSelection();
    const range = selection?.getRangeAt(0);

    const boundingRect = range?.getBoundingClientRect();
    const selectionTop = Math.round(Math.max(0, boundingRect?.top || 0));
    const selectionLeft = Math.round(Math.max(0, boundingRect?.left || 0));

    const editorBoundingRect = containerRef.current?.getBoundingClientRect();
    const editorTop = Math.round(Math.max(0, editorBoundingRect?.top || 0));
    const editorBottom = Math.round(Math.max(0, editorBoundingRect?.bottom || 0));
    const editorLeft = Math.round(Math.max(0, editorBoundingRect?.left || 0));
    const editorRight = Math.round(Math.max(0, editorBoundingRect?.right || 0));

    const top = Math.min(Math.max(selectionTop, editorTop), editorBottom);
    const left = Math.min(Math.max(selectionLeft, editorLeft), editorRight);

    return { top, left };
};

export const mod = (m: number, n: number): number => {
    return ((m % n) + n) % n;
};

export const findVariable = (
    variable: string,
    variables: RichEditorVariable[] = []
): RichEditorVariable | undefined => {
    return variables.find((item) =>
        Array.isArray(item.name)
            ? item.name.find((name) => name.toLowerCase() === variable.toLowerCase())
            : item.name.toLowerCase() === variable.toLowerCase()
    );
};
